import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

export interface SegmentedControlOption<T extends string | number> {
  value: T;
  label: ReactNode;
}

export interface SegmentedControlProps<T extends string | number> {
  /** Accessible role for the container. Use "tablist" for tab UIs, "radiogroup"
   *  when the choice is a single selection (e.g. font style). */
  role?: "tablist" | "radiogroup";
  ariaLabel: string;
  value: T;
  options: ReadonlyArray<SegmentedControlOption<T>>;
  onChange: (value: T) => void;
  /** "default" for primary navigation (e.g. mode tabs);
   *  "sm" for a denser variant (e.g. nested sub-tabs). */
  size?: "default" | "sm";
  className?: string;
}

/**
 * Pill-shaped segmented control. Active item gets primary background;
 * inactive items stay transparent and pick up hover state.
 */
export function SegmentedControl<T extends string | number>({
  role = "radiogroup",
  ariaLabel,
  value,
  options,
  onChange,
  size = "default",
  className,
}: SegmentedControlProps<T>) {
  const isTablist = role === "tablist";
  const sizing =
    size === "sm"
      ? "p-0.5 text-xs"
      : "p-1 text-sm";
  const buttonSizing =
    size === "sm"
      ? "px-3 py-1"
      : "px-4 py-1.5";

  return (
    <div
      role={role}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex w-fit items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)]",
        sizing,
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const itemRole = isTablist ? "tab" : "radio";
        return (
          <button
            key={String(opt.value)}
            type="button"
            role={itemRole}
            aria-selected={isTablist ? selected : undefined}
            aria-checked={!isTablist ? selected : undefined}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-full transition-colors",
              buttonSizing,
              selected
                ? "bg-[color:var(--primary)] text-[color:var(--bg)]"
                : "bg-transparent text-[color:var(--text-muted)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text)]",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}